import { signal } from '@preact/signals-core';
import { activeRoleSignal, getAuthHeaders, customersSignal, searchCustomersForAutocomplete, sitesSignal } from './assets';

export const usersListSignal = signal<any[]>([]);
export const selectedUserIdSignal = signal<string | null>(null);
export const usersSearchQuerySignal = signal<string>('');
export const usersRoleFilterSignal = signal<string>('all');

export async function refreshUsers() {
	try {
		const roleFilter = usersRoleFilterSignal.value;
		let url = '/api/v1/users?limit=1000';
		if (roleFilter && roleFilter !== 'all') {
			url += `&role=${roleFilter}`;
		}
		const res = await fetch(url, { headers: getAuthHeaders() });
		if (res.ok) {
			usersListSignal.value = await res.json();
		} else {
			usersListSignal.value = [];
		}
	} catch (e) {
		console.error('Failed to fetch users', e);
	}
}

export function renderUsersPage(): string {
	const role = activeRoleSignal.value;
	const isAuthorized = role === 'superadmin' || role === 'admin' || role === 'manager';
	const isReadOnly = role === 'manager';

	if (!isAuthorized) {
		return `
			<s-page heading="Access Denied" inlineSize="large">
				<s-section>
					<s-box padding="base" background="surface" border="base" borderRadius="base">
						<s-banner tone="critical">
							You do not have permissions to access the User Directory page. Contact your administrator if this is an error.
						</s-banner>
					</s-box>
				</s-section>
			</s-page>
		`;
	}

	const query = usersSearchQuerySignal.value.toLowerCase().trim();
	const allUsers = usersListSignal.value || [];
	const filteredUsers = allUsers.filter(u => u.email.toLowerCase().includes(query));

	const customers = customersSignal.value || [];
	const sites = sitesSignal.value || [];

	const custMap = new Map(customers.map(c => [c.id, c.name]));
	const siteMap = new Map(sites.map(s => [s.id, s.name]));

	const tableRowsHtml = filteredUsers.map(u => {
		const orgName = u.customerId ? (custMap.get(u.customerId) || 'Unknown Org') : 'System-wide';
		const assignedSites = (u.sites || []).map((sId: string) => siteMap.get(sId) || 'Unknown Site');
		const sitesHtml = assignedSites.length === 0
			? '<s-text tone="subdued">None</s-text>'
			: assignedSites.map((name: string) => `<s-badge>${name}</s-badge>`).join(' ');

		let badgeTone = '';
		if (u.role === 'superadmin') badgeTone = 'critical';
		else if (u.role === 'admin') badgeTone = 'attention';
		else if (u.role === 'manager') badgeTone = 'info';
		else if (u.role === 'technician') badgeTone = 'success';

		const dateStr = new Date(u.createdAt).toLocaleDateString();

		return `
			<s-table-row>
				<s-table-cell><strong>${u.email}</strong></s-table-cell>
				<s-table-cell><s-badge ${badgeTone ? `tone="${badgeTone}"` : ''}>${u.role}</s-badge></s-table-cell>
				<s-table-cell>${orgName}</s-table-cell>
				<s-table-cell>
					<div style="display:flex; flex-wrap:wrap; gap:0.25rem;">
						${sitesHtml}
					</div>
				</s-table-cell>
				<s-table-cell>${dateStr}</s-table-cell>
				<s-table-cell>
					${!isReadOnly ? `
						<s-button id="btn-edit-user-${u.id}" size="slim">Edit</s-button>
						<s-button id="btn-reset-user-${u.id}" size="slim">Reset PW</s-button>
						<s-button id="btn-delete-user-${u.id}" tone="critical" size="slim">Delete</s-button>
					` : `<s-text tone="subdued">Read-only</s-text>`}
				</s-table-cell>
			</s-table-row>
		`;
	}).join('');

	return `
		<s-page heading="User Management" subtitle="Configure system accounts, roles, organization boundaries, and site access context." inlineSize="large">
			${!isReadOnly ? `
				<div slot="primaryAction">
					<s-button variant="primary" id="btn-open-create-user">Add User</s-button>
				</div>
			` : ''}

			<!-- Filters Section -->
			<s-section>
				<s-box padding="base" background="surface" border="base" borderRadius="base">
					<s-stack gap="base">
						<s-stack direction="inline" gap="base" wrap>
							<div style="flex: 2; min-width: 250px;">
								<s-text-field 
									label="Search Users" 
									id="users-search-input" 
									value="${usersSearchQuerySignal.value}" 
									placeholder="Search by email..."
									name="search"
								></s-text-field>
							</div>
							<div style="flex: 1; min-width: 180px;">
								<label style="display:block; font-size:0.875rem; font-weight:500; margin-bottom:0.25rem;">Filter by Role</label>
								<select id="filter-user-role" class="polaris-select-custom" style="width:100%; padding:0.5rem; border:1px solid var(--p-color-border-subdued); border-radius:4px; background:var(--p-color-bg-surface); height:38px;">
									<option value="all" ${usersRoleFilterSignal.value === 'all' ? 'selected' : ''}>All Roles</option>
									<option value="superadmin" ${usersRoleFilterSignal.value === 'superadmin' ? 'selected' : ''}>Superadmin</option>
									<option value="admin" ${usersRoleFilterSignal.value === 'admin' ? 'selected' : ''}>Admin</option>
									<option value="manager" ${usersRoleFilterSignal.value === 'manager' ? 'selected' : ''}>Manager</option>
									<option value="technician" ${usersRoleFilterSignal.value === 'technician' ? 'selected' : ''}>Technician</option>
									<option value="viewer" ${usersRoleFilterSignal.value === 'viewer' ? 'selected' : ''}>Viewer</option>
								</select>
							</div>
						</s-stack>
					</s-stack>
				</s-box>
			</s-section>

			<!-- Users List Section -->
			<s-section>
				<s-box padding="none" background="surface" border="base" borderRadius="base">
					${filteredUsers.length === 0 ? `
						<div style="padding: 3rem; text-align: center;">
							<s-text tone="subdued">No users matched the criteria.</s-text>
						</div>
					` : `
						<s-table>
							<s-table-header-row>
								<s-table-header>Email</s-table-header>
								<s-table-header>Role</s-table-header>
								<s-table-header>Organization</s-table-header>
								<s-table-header>Assigned Sites</s-table-header>
								<s-table-header>Created At</s-table-header>
								<s-table-header>Actions</s-table-header>
							</s-table-header-row>
							<s-table-body>
								${tableRowsHtml}
							</s-table-body>
						</s-table>
					`}
				</s-box>
			</s-section>

			<!-- Create User Modal -->
			<s-modal id="create-user-modal" heading="Register New User Account">
				<s-form id="form-create-user">
					<s-stack gap="base">
						<s-email-field label="Email Address" name="email" id="create-user-email" required placeholder="e.g. user@example.com"></s-email-field>
						<s-password-field label="Initial Password" name="password" id="create-user-password" required placeholder="Minimum 8 characters"></s-password-field>
						
						<div style="display:flex; flex-direction:column; gap:0.25rem;">
							<label style="font-size:0.875rem; font-weight:500;">Role Designation</label>
							<select name="role" id="create-user-role-select" style="padding:0.5rem; border:1px solid var(--p-color-border-subdued); border-radius:4px; background:var(--p-color-bg-surface); height:38px;">
								<option value="viewer">Viewer</option>
								<option value="technician">Technician</option>
								<option value="manager">Manager</option>
								<option value="admin">Admin</option>
								<option value="superadmin">Superadmin</option>
							</select>
						</div>

						<div style="display:flex; flex-direction:column; gap:0.25rem;" id="create-user-customer-wrapper">
							<label style="font-size:0.875rem; font-weight:500;">Customer Organization</label>
							<opm-autocomplete id="create-user-customer-select" label="Customer Organization" placeholder="Search customers..."></opm-autocomplete>
							<input type="hidden" name="customerId" id="create-user-customer-id" />
						</div>

						<div style="display:none; flex-direction:column; gap:0.25rem;" id="create-user-sites-wrapper">
							<label style="font-size:0.875rem; font-weight:500;">Assigned Sites</label>
							<div id="create-user-sites-checkboxes" style="max-height:150px; overflow-y:auto; border:1px solid var(--p-color-border-subdued); padding:0.5rem; border-radius:4px; display:flex; flex-direction:column; gap:0.25rem;">
								<!-- Checkboxes rendered dynamically -->
							</div>
						</div>

						<div id="create-user-error-banner" style="display:none; color:var(--p-color-text-critical); margin-top:0.5rem; font-size:0.875rem;"></div>

						<s-stack direction="inline" gap="small" style="margin-top:1.5rem;">
							<s-button variant="primary" id="btn-submit-create-user">Create Account</s-button>
							<s-button commandFor="create-user-modal" command="--hide">Cancel</s-button>
						</s-stack>
					</s-stack>
				</s-form>
			</s-modal>

			<!-- Edit User Modal -->
			<s-modal id="edit-user-modal" heading="Modify User Settings">
				<s-form id="form-edit-user">
					<s-stack gap="base">
						<s-email-field label="Email Address" name="email" id="edit-user-email" required></s-email-field>
						
						<div style="display:flex; flex-direction:column; gap:0.25rem;">
							<label style="font-size:0.875rem; font-weight:500;">Role Designation</label>
							<select name="role" id="edit-user-role-select" style="padding:0.5rem; border:1px solid var(--p-color-border-subdued); border-radius:4px; background:var(--p-color-bg-surface); height:38px;">
								<option value="viewer">Viewer</option>
								<option value="technician">Technician</option>
								<option value="manager">Manager</option>
								<option value="admin">Admin</option>
								<option value="superadmin">Superadmin</option>
							</select>
						</div>

						<div style="display:flex; flex-direction:column; gap:0.25rem;" id="edit-user-customer-wrapper">
							<label style="font-size:0.875rem; font-weight:500;">Customer Organization</label>
							<opm-autocomplete id="edit-user-customer-select" label="Customer Organization" placeholder="Search customers..."></opm-autocomplete>
							<input type="hidden" name="customerId" id="edit-user-customer-id" />
						</div>

						<div style="display:none; flex-direction:column; gap:0.25rem;" id="edit-user-sites-wrapper">
							<label style="font-size:0.875rem; font-weight:500;">Assigned Sites</label>
							<div id="edit-user-sites-checkboxes" style="max-height:150px; overflow-y:auto; border:1px solid var(--p-color-border-subdued); padding:0.5rem; border-radius:4px; display:flex; flex-direction:column; gap:0.25rem;">
								<!-- Checkboxes rendered dynamically -->
							</div>
						</div>

						<div id="edit-user-error-banner" style="display:none; color:var(--p-color-text-critical); margin-top:0.5rem; font-size:0.875rem;"></div>

						<s-stack direction="inline" gap="small" style="margin-top:1.5rem;">
							<s-button variant="primary" id="btn-submit-edit-user">Save Changes</s-button>
							<s-button commandFor="edit-user-modal" command="--hide">Cancel</s-button>
						</s-stack>
					</s-stack>
				</s-form>
			</s-modal>

			<!-- Reset Password Modal -->
			<s-modal id="reset-password-modal" heading="Force Password Reset">
				<s-form id="form-reset-password">
					<s-stack gap="base">
						<s-text>Configure a new system access password for user: <strong id="reset-user-email-target"></strong>.</s-text>
						<s-password-field label="New Password" name="password" id="reset-user-password-input" required placeholder="Minimum 8 characters"></s-password-field>
						<div id="reset-user-error-banner" style="display:none; color:var(--p-color-text-critical); margin-top:0.5rem; font-size:0.875rem;"></div>

						<s-stack direction="inline" gap="small" style="margin-top:1.5rem;">
							<s-button variant="primary" tone="critical" id="btn-submit-reset-password">Reset Password</s-button>
							<s-button commandFor="reset-password-modal" command="--hide">Cancel</s-button>
						</s-stack>
					</s-stack>
				</s-form>
			</s-modal>

			<!-- Delete User Confirmation Modal -->
			<s-modal id="delete-user-modal" heading="Remove User Account?">
				<s-stack gap="base">
					<s-text tone="critical">Are you sure you want to delete <strong id="delete-user-email-display"></strong>? This operation will revoke active session access immediately.</s-text>
					<div id="delete-user-error-banner" style="display:none; color:var(--p-color-text-critical); margin-top:0.5rem; font-size:0.875rem;"></div>

					<s-stack direction="inline" gap="small" style="margin-top:1.5rem;">
						<s-button variant="primary" tone="critical" id="btn-confirm-delete-user">Delete Account</s-button>
						<s-button commandFor="delete-user-modal" command="--hide">Cancel</s-button>
					</s-stack>
				</s-stack>
			</s-modal>
		</s-page>
	`;
}

export function setupUsersPageListeners() {
	const role = activeRoleSignal.value;
	const isReadOnly = role === 'manager';

	// 1. Search filter keydown/input listener
	const searchInput = document.getElementById('users-search-input');
	if (searchInput) {
		searchInput.addEventListener('input', (e: any) => {
			usersSearchQuerySignal.value = e.target.value;
			triggerAppReRender();
		});
	}

	// 2. Role filter select listener
	const roleSelect = document.getElementById('filter-user-role');
	if (roleSelect) {
		roleSelect.addEventListener('change', async (e: any) => {
			usersRoleFilterSignal.value = e.target.value;
			await refreshUsers();
			triggerAppReRender();
		});
	}

	if (isReadOnly) return; // managers can't perform modifying actions

	// Helper: Update site checkboxes in Create modal based on organization selection
	const updateCreateSitesCheckboxes = () => {
		const customerSelect = document.getElementById('create-user-customer-select') as any;
		const container = document.getElementById('create-user-sites-checkboxes');
		const wrapper = document.getElementById('create-user-sites-wrapper');
		if (!customerSelect || !container || !wrapper) return;

		const customerId = customerSelect.selectedId || '';
		if (!customerId) {
			wrapper.style.display = 'none';
			container.innerHTML = '';
			return;
		}

		wrapper.style.display = 'flex';
		const filtered = (sitesSignal.value || []).filter(s => s.customerId === customerId);
		if (filtered.length === 0) {
			container.innerHTML = '<s-text tone="subdued">No sites configured for this customer organization.</s-text>';
		} else {
			container.innerHTML = filtered.map(s => `
				<s-checkbox label="${s.name}" id="create-user-site-${s.id}" value="${s.id}"></s-checkbox>
			`).join('');
		}
	};

	// Helper: Update site checkboxes in Edit modal
	const updateEditSitesCheckboxes = (selectedSiteIds: string[] = []) => {
		const customerSelect = document.getElementById('edit-user-customer-select') as any;
		const container = document.getElementById('edit-user-sites-checkboxes');
		const wrapper = document.getElementById('edit-user-sites-wrapper');
		if (!customerSelect || !container || !wrapper) return;

		const customerId = customerSelect.selectedId || '';
		if (!customerId) {
			wrapper.style.display = 'none';
			container.innerHTML = '';
			return;
		}

		wrapper.style.display = 'flex';
		const filtered = (sitesSignal.value || []).filter(s => s.customerId === customerId);
		if (filtered.length === 0) {
			container.innerHTML = '<s-text tone="subdued">No sites configured for this customer organization.</s-text>';
		} else {
			container.innerHTML = filtered.map(s => {
				const isChecked = selectedSiteIds.includes(s.id);
				return `
					<s-checkbox label="${s.name}" id="edit-user-site-${s.id}" value="${s.id}" ${isChecked ? 'checked' : ''}></s-checkbox>
				`;
			}).join('');
		}
	};

	// Bind change listeners to customer select dropdowns
	const createCustSelect = document.getElementById('create-user-customer-select');
	if (createCustSelect) {
		(createCustSelect as any).loadItems = searchCustomersForAutocomplete;
		createCustSelect.addEventListener('select', () => updateCreateSitesCheckboxes());
		createCustSelect.addEventListener('clear', () => updateCreateSitesCheckboxes());
	}

	const editCustSelect = document.getElementById('edit-user-customer-select');
	if (editCustSelect) {
		(editCustSelect as any).loadItems = searchCustomersForAutocomplete;
		editCustSelect.addEventListener('select', () => updateEditSitesCheckboxes());
		editCustSelect.addEventListener('clear', () => updateEditSitesCheckboxes());
	}

	// 3. Open Create Modal
	const openCreateBtn = document.getElementById('btn-open-create-user');
	if (openCreateBtn) {
		openCreateBtn.addEventListener('click', () => {
			const emailField = document.getElementById('create-user-email') as any;
			const passwordField = document.getElementById('create-user-password') as any;
			const errDiv = document.getElementById('create-user-error-banner');

			if (emailField) emailField.value = '';
			if (passwordField) passwordField.value = '';
			if (errDiv) errDiv.style.display = 'none';

			const modal = document.getElementById('create-user-modal') as any;
			if (modal && typeof modal.showOverlay === 'function') {
				modal.showOverlay();
			}
			updateCreateSitesCheckboxes();
		});
	}

	// 4. Submit Create User Form
	const submitCreateBtn = document.getElementById('btn-submit-create-user');
	if (submitCreateBtn) {
		submitCreateBtn.addEventListener('click', async (e) => {
			e.preventDefault();
			const form = document.getElementById('form-create-user') as HTMLFormElement;
			if (!form) return;

			const emailEl = document.getElementById('create-user-email') as any;
			const passwordEl = document.getElementById('create-user-password') as any;
			const roleEl = document.getElementById('create-user-role-select') as HTMLSelectElement;
			const customerEl = document.getElementById('create-user-customer-select') as any;

			const email = emailEl?.value || '';
			const password = passwordEl?.value || '';
			const userRole = roleEl?.value || 'viewer';
			const customerId = customerEl?.selectedId || null;

			const errDiv = document.getElementById('create-user-error-banner');
			if (errDiv) errDiv.style.display = 'none';

			if (!email || !password) {
				if (errDiv) {
					errDiv.textContent = 'Email and password are required.';
					errDiv.style.display = 'block';
				}
				return;
			}
			if (password.length < 8) {
				if (errDiv) {
					errDiv.textContent = 'Password must be at least 8 characters long.';
					errDiv.style.display = 'block';
				}
				return;
			}

			// Gather checked sites
			const checkedSiteIds: string[] = [];
			if (customerId) {
				const filteredSites = (sitesSignal.value || []).filter(s => s.customerId === customerId);
				filteredSites.forEach(s => {
					const cb = document.getElementById(`create-user-site-${s.id}`) as any;
					if (cb && cb.checked) {
						checkedSiteIds.push(s.id);
					}
				});
			}

			try {
				const res = await fetch('/api/v1/users', {
					method: 'POST',
					headers: getAuthHeaders(),
					body: JSON.stringify({
						email,
						password,
						role: userRole,
						customerId,
						sites: checkedSiteIds,
					}),
				});

				if (res.ok) {
					const modal = document.getElementById('create-user-modal') as any;
					if (modal && typeof modal.hideOverlay === 'function') {
						modal.hideOverlay();
					}
					await refreshUsers();
					triggerAppReRender();
				} else {
					const data = await res.json();
					if (errDiv) {
						errDiv.textContent = data.error || 'Failed to create user.';
						errDiv.style.display = 'block';
					}
				}
			} catch (err) {
				if (errDiv) {
					errDiv.textContent = 'Network failure.';
					errDiv.style.display = 'block';
				}
			}
		});
	}

	// 5. Attach Row Actions (Edit, Reset PW, Delete)
	const users = usersListSignal.value || [];
	users.forEach(u => {
		// Edit Row Button
		const editBtn = document.getElementById(`btn-edit-user-${u.id}`);
		if (editBtn) {
			editBtn.addEventListener('click', () => {
				selectedUserIdSignal.value = u.id;

				const emailEl = document.getElementById('edit-user-email') as any;
				if (emailEl) emailEl.value = u.email;

				const roleEl = document.getElementById('edit-user-role-select') as HTMLSelectElement;
				if (roleEl) roleEl.value = u.role;

				const customerEl = document.getElementById('edit-user-customer-select') as any;
				if (customerEl) customerEl.selectedId = u.customerId || null;

				const errDiv = document.getElementById('edit-user-error-banner');
				if (errDiv) errDiv.style.display = 'none';

				const modal = document.getElementById('edit-user-modal') as any;
				if (modal && typeof modal.showOverlay === 'function') {
					modal.showOverlay();
				}
				updateEditSitesCheckboxes(u.sites || []);
			});
		}

		// Reset PW Row Button
		const resetBtn = document.getElementById(`btn-reset-user-${u.id}`);
		if (resetBtn) {
			resetBtn.addEventListener('click', () => {
				selectedUserIdSignal.value = u.id;

				const targetEmailEl = document.getElementById('reset-user-email-target');
				if (targetEmailEl) targetEmailEl.textContent = u.email;

				const passwordEl = document.getElementById('reset-user-password-input') as any;
				if (passwordEl) passwordEl.value = '';

				const errDiv = document.getElementById('reset-user-error-banner');
				if (errDiv) errDiv.style.display = 'none';

				const modal = document.getElementById('reset-password-modal') as any;
				if (modal && typeof modal.showOverlay === 'function') {
					modal.showOverlay();
				}
			});
		}

		// Delete Row Button
		const deleteBtn = document.getElementById(`btn-delete-user-${u.id}`);
		if (deleteBtn) {
			deleteBtn.addEventListener('click', () => {
				selectedUserIdSignal.value = u.id;

				const targetDisplayEl = document.getElementById('delete-user-email-display');
				if (targetDisplayEl) targetDisplayEl.textContent = u.email;

				const errDiv = document.getElementById('delete-user-error-banner');
				if (errDiv) errDiv.style.display = 'none';

				const modal = document.getElementById('delete-user-modal') as any;
				if (modal && typeof modal.showOverlay === 'function') {
					modal.showOverlay();
				}
			});
		}
	});

	// 6. Submit Edit User Form
	const submitEditBtn = document.getElementById('btn-submit-edit-user');
	if (submitEditBtn) {
		submitEditBtn.addEventListener('click', async (e) => {
			e.preventDefault();
			const id = selectedUserIdSignal.value;
			if (!id) return;

			const emailEl = document.getElementById('edit-user-email') as any;
			const roleEl = document.getElementById('edit-user-role-select') as HTMLSelectElement;
			const customerEl = document.getElementById('edit-user-customer-select') as any;

			const email = emailEl?.value || '';
			const userRole = roleEl?.value || 'viewer';
			const customerId = customerEl?.selectedId || null;

			const errDiv = document.getElementById('edit-user-error-banner');
			if (errDiv) errDiv.style.display = 'none';

			if (!email) {
				if (errDiv) {
					errDiv.textContent = 'Email address is required.';
					errDiv.style.display = 'block';
				}
				return;
			}

			// Gather checked sites
			const checkedSiteIds: string[] = [];
			if (customerId) {
				const filteredSites = (sitesSignal.value || []).filter(s => s.customerId === customerId);
				filteredSites.forEach(s => {
					const cb = document.getElementById(`edit-user-site-${s.id}`) as any;
					if (cb && cb.checked) {
						checkedSiteIds.push(s.id);
					}
				});
			}

			try {
				const res = await fetch(`/api/v1/users/${id}`, {
					method: 'PUT',
					headers: getAuthHeaders(),
					body: JSON.stringify({
						email,
						role: userRole,
						customerId,
						sites: checkedSiteIds,
					}),
				});

				if (res.ok) {
					const modal = document.getElementById('edit-user-modal') as any;
					if (modal && typeof modal.hideOverlay === 'function') {
						modal.hideOverlay();
					}
					await refreshUsers();
					triggerAppReRender();
				} else {
					const data = await res.json();
					if (errDiv) {
						errDiv.textContent = data.error || 'Failed to update user settings.';
						errDiv.style.display = 'block';
					}
				}
			} catch (err) {
				if (errDiv) {
					errDiv.textContent = 'Network failure.';
					errDiv.style.display = 'block';
				}
			}
		});
	}

	// 7. Submit Reset Password Form
	const submitResetBtn = document.getElementById('btn-submit-reset-password');
	if (submitResetBtn) {
		submitResetBtn.addEventListener('click', async (e) => {
			e.preventDefault();
			const id = selectedUserIdSignal.value;
			if (!id) return;

			const passwordEl = document.getElementById('reset-user-password-input') as any;
			const password = passwordEl?.value || '';

			const errDiv = document.getElementById('reset-user-error-banner');
			if (errDiv) errDiv.style.display = 'none';

			if (!password) {
				if (errDiv) {
					errDiv.textContent = 'Password is required.';
					errDiv.style.display = 'block';
				}
				return;
			}
			if (password.length < 8) {
				if (errDiv) {
					errDiv.textContent = 'Password must be at least 8 characters long.';
					errDiv.style.display = 'block';
				}
				return;
			}

			try {
				const res = await fetch(`/api/v1/users/${id}/reset-password`, {
					method: 'POST',
					headers: getAuthHeaders(),
					body: JSON.stringify({ password }),
				});

				if (res.ok) {
					const modal = document.getElementById('reset-password-modal') as any;
					if (modal && typeof modal.hideOverlay === 'function') {
						modal.hideOverlay();
					}
					await refreshUsers();
					triggerAppReRender();
				} else {
					const data = await res.json();
					if (errDiv) {
						errDiv.textContent = data.error || 'Failed to reset password.';
						errDiv.style.display = 'block';
					}
				}
			} catch (err) {
				if (errDiv) {
					errDiv.textContent = 'Network failure.';
					errDiv.style.display = 'block';
				}
			}
		});
	}

	// 8. Confirm Delete User Action
	const confirmDeleteBtn = document.getElementById('btn-confirm-delete-user');
	if (confirmDeleteBtn) {
		confirmDeleteBtn.addEventListener('click', async () => {
			const id = selectedUserIdSignal.value;
			if (!id) return;

			const errDiv = document.getElementById('delete-user-error-banner');
			if (errDiv) errDiv.style.display = 'none';

			try {
				const res = await fetch(`/api/v1/users/${id}`, {
					method: 'DELETE',
					headers: getAuthHeaders(),
				});

				if (res.ok) {
					const modal = document.getElementById('delete-user-modal') as any;
					if (modal && typeof modal.hideOverlay === 'function') {
						modal.hideOverlay();
					}
					await refreshUsers();
					triggerAppReRender();
				} else {
					const data = await res.json();
					if (errDiv) {
						errDiv.textContent = data.error || 'Failed to delete user account.';
						errDiv.style.display = 'block';
					}
				}
			} catch (err) {
				if (errDiv) {
					errDiv.textContent = 'Network failure.';
					errDiv.style.display = 'block';
				}
			}
		});
	}
}

function triggerAppReRender() {
	const ev = new HashChangeEvent('hashchange');
	window.dispatchEvent(ev);
}
